// The 14 allergens UK law names, and the words the menu already uses for them. The server
// keeps the same list in `backend/allergens.py`; a backend test holds the two equal.
//
// A table's allergens are taps from this list. A dish's are read through `canonical`, which
// knows the older words: the back office wrote `dairy`, `nuts` and `shellfish`, the seed and
// Bridge imports write `milk`, `molluscs`, `soy`. `shellfish` maps to both crustaceans and
// molluscs, because it could mean either and the safe reading is the wider one.
//
// A word this list cannot read ("butter", "nuts (almond)", "kiwi") is not an allergen the
// till knows, and it is not the absence of one either. `unread` returns those words as
// written, and everywhere a clash is shown they are shown too, as something to check: the
// till cannot call a dish safe for a table on the strength of a word it did not understand.

export const ALLERGENS = [
  { key: 'celery', label: 'Celery' },
  { key: 'gluten', label: 'Gluten' },
  { key: 'crustaceans', label: 'Crustaceans' },
  { key: 'eggs', label: 'Eggs' },
  { key: 'fish', label: 'Fish' },
  { key: 'lupin', label: 'Lupin' },
  { key: 'milk', label: 'Milk' },
  { key: 'molluscs', label: 'Molluscs' },
  { key: 'mustard', label: 'Mustard' },
  { key: 'tree_nuts', label: 'Tree nuts' },
  { key: 'peanuts', label: 'Peanuts' },
  { key: 'sesame', label: 'Sesame' },
  { key: 'soy', label: 'Soya' },
  { key: 'sulphites', label: 'Sulphites' },
];

const ORDER = Object.fromEntries(ALLERGENS.map((a, i) => [a.key, i]));
const LABEL = Object.fromEntries(ALLERGENS.map((a) => [a.key, a.label]));

export const ALIASES = {
  dairy: ['milk'], lactose: ['milk'],
  nuts: ['tree_nuts'], nut: ['tree_nuts'], 'tree nuts': ['tree_nuts'], treenuts: ['tree_nuts'], tree_nut: ['tree_nuts'],
  'tree nut': ['tree_nuts'],
  peanut: ['peanuts'],
  shellfish: ['crustaceans', 'molluscs'],
  crustacean: ['crustaceans'], mollusc: ['molluscs'],
  egg: ['eggs'],
  soya: ['soy'],
  sulfites: ['sulphites'], 'sulphur dioxide': ['sulphites'], sulphur_dioxide: ['sulphites'],
  wheat: ['gluten'], cereals: ['gluten'], 'cereals containing gluten': ['gluten'],
};

/** The keys one word stands for; [] for a blank, null for a word nobody here knows. */
function read(w) {
  const word = String(w ?? '').trim().toLowerCase();
  if (!word) return [];
  if (word in ORDER) return [word];
  return ALIASES[word] || ALIASES[word.replace(/-/g, ' ')] || null;
}

/** Canonical keys for `words`, deduplicated, in the law's order. A word that is not one of
 * the 14 is left out: it can never be tapped on a table. It is not forgotten: see `unread`. */
export function canonical(words) {
  const out = new Set();
  for (const w of words || []) (read(w) || []).forEach((k) => out.add(k));
  return [...out].sort((a, b) => ORDER[a] - ORDER[b]);
}

/** The words in `words` this list cannot read, as written, each once. Never a reason to
 * call a dish clear: at a table with an allergy they are a reason to check. */
export function unread(words) {
  const out = [];
  const seen = new Set();
  for (const w of words || []) {
    if (read(w) !== null) continue;
    const text = String(w).trim();
    if (seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    out.push(text);
  }
  return out;
}

export const allergenLabel = (key) => LABEL[key] || key;

/** "Gluten, eggs, sesame, milk": a dish's allergens as a phrase for a card. Words that are
 * not one of the 14 are kept, as written, so nothing on the dish disappears. */
export function describe(words) {
  const all = [...canonical(words).map(allergenLabel), ...unread(words)];
  if (!all.length) return '';
  return all.map((w, i) => (i === 0 ? w : w.toLowerCase())).join(', ');
}

/** Every allergen word on a dish. A meal's are its own and those of every option it offers:
 * the card is marked before the guest has chosen, because "it depends" is a check. */
export function dishWords(item, menuById = {}) {
  const words = [...(item?.allergens || [])];
  for (const group of item?.meal_groups || []) {
    for (const option of group?.options || []) {
      words.push(...(menuById[option?.menu_item_id]?.allergens || []));
    }
  }
  return words;
}

/** Every allergen word on a line of the order: the dish, and for a meal the parts chosen. */
export function lineWords(line, menuById = {}) {
  const words = [...(menuById[line?.menu_item_id]?.allergens || line?.allergens || [])];
  for (const c of line?.components || []) {
    words.push(...(menuById[c?.menu_item_id]?.allergens || []));
  }
  return words;
}

/** Everything a dish could contain, of the 14. */
export const dishAllergens = (item, menuById = {}) => canonical(dishWords(item, menuById));

/** What a line on the order actually contains, of the 14. */
export const lineAllergens = (line, menuById = {}) => canonical(lineWords(line, menuById));

/** The table's allergens these contain, in the law's order. */
export function clash(contains, tableAllergens) {
  const mine = new Set(canonical(contains));
  return canonical(tableAllergens).filter((k) => mine.has(k));
}

/** "Milk" / "milk and eggs" / "milk, eggs and gluten": for a sentence. */
export function listWords(keys, { capitalise = false } = {}) {
  const words = keys.map((k, i) => {
    const l = allergenLabel(k);
    return i === 0 && capitalise ? l : l.toLowerCase();
  });
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}
